import { FormOnChange } from '@qfa/forms';
import { useLatest } from '@qfa/react-components';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EditableTitleProps, useEditableTitleOptions } from './types';

/**
 * @param `useEditableTitleOptions`
 *
 * title: the text displayed when not in editing mode.
 *
 * onSave: callback for handling the new title, normally a setState Dispatch.
 *
 * initialEditMode?: defaulted to false.
 *
 * onCancel?: callback for handling the user canceling the title change.
 *
 * @returns `EditableTitleProps` to pass to the EditableTitle component
 */
export const useEditableTitle = ({
  initialEditMode = false,
  title,
  onSave,
  onCancel,
}: useEditableTitleOptions): Required<
  Pick<
    EditableTitleProps,
    | 'title'
    | 'inputValue'
    | 'isEditMode'
    | 'inputRef'
    | 'onChange'
    | 'onSave'
    | 'onCancel'
    | 'onEdit'
    | 'setInputValue'
  >
> => {
  const [isEditMode, setEditMode] = useState(initialEditMode);
  const [inputValue, setInputValue] = useState(title);

  const previousTitle = useLatest(title);
  const latestInputValue = useLatest(inputValue);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let editModeTimeout: ReturnType<typeof setTimeout>;
    if (isEditMode) {
      editModeTimeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 20);
    }
    return () => clearTimeout(editModeTimeout);
  }, [isEditMode]);

  // just incase somebody changes the title value outside of this scope.
  useEffect(() => {
    if (title !== latestInputValue.current) {
      setInputValue(title);
    }
  }, [title, latestInputValue]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const onCancelClick = useCallback(() => {
    onCancel?.();
    setInputValue(previousTitle.current);
    setEditMode(false);
  }, [onCancel, previousTitle]);

  const onSaveClick = useCallback(() => {
    if (latestInputValue.current.trim().length) {
      onSave(latestInputValue.current);
      setEditMode(false);
    } else {
      onCancelClick();
    }
  }, [latestInputValue, onCancelClick, onSave]);

  const onTitleClick = useCallback(() => {
    setEditMode(true);
  }, []);

  return {
    title,
    inputValue,
    isEditMode,
    inputRef,
    onChange,
    onSave: onSaveClick,
    onCancel: onCancelClick,
    onEdit: onTitleClick,
    setInputValue,
  };
};

export const useEditableTitleFormValue = (
  fieldName: string,
  dirtyFields: string[],
  onChange: FormOnChange
) => {
  const onNameChange = useCallback(
    (newName: string) => {
      const newDirtyFields = dirtyFields.includes(fieldName)
        ? dirtyFields
        : [...dirtyFields, fieldName];
      onChange({ name: newName }, [fieldName], newDirtyFields);
    },
    [dirtyFields, onChange, fieldName]
  );

  return onNameChange;
};
